import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { RiskAssessmentResultControlType } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { InsertRiskInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertRiskInternalAuditResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export type InsertRiskInternalAuditResultInput = {
  InternalAuditReportId: string;
  RiskIds: string[];
  ControlType: RiskAssessmentResultControlType;
  Rating?: number | null;
  Impact?: number | null;
  Likelihood?: number | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useInsertRiskInternalAuditResult = createMutationHook({
  name: 'insertRiskInternalAuditResult',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.internalAuditResult.insertRisk.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (input: InsertRiskInternalAuditResultInput) => input,
  mapResponse: (result: {
    Ids: string[];
  }): InsertRiskInternalAuditResultMutation => ({
    insertChildRiskInternalAuditResult: {
      __typename: 'InsertChildRiskAssessmentResultOutput',
      Ids: result.Ids,
    },
  }),
  invalidate: (trpc) => [
    trpc.frontend.internalAuditResult.pathKey(),
    trpc.frontend.assessment.internalAuditResultCount.queryKey(),
    // COI-471 parity: Apollo twin also evicts `internal_audit_report`.
    trpc.frontend.internalAuditReport.pathKey(),
  ],
  graphqlDocument: InsertRiskInternalAuditResultDocument,
  graphqlVariables: (input: InsertRiskInternalAuditResultInput) => ({
    ...input,
  }),
  evictFields: [
    'risk_controlled_internal_audit_result',
    'risk_uncontrolled_internal_audit_result',
    'internal_audit_report',
    'risk_controlled_internal_audit_result_aggregate',
    'risk_uncontrolled_internal_audit_result_aggregate',
  ],
});
